// soccer_admin/src/pages/Cards.jsx
import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { fetchAdminUsers, fetchAdminCards, fetchUserCards } from '../services/api';

const Cards = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userCards, setUserCards] = useState([]);
  const [allCards, setAllCards] = useState([]);
  const [viewMode, setViewMode] = useState('all');
  const [loading, setLoading] = useState(true);

  // Pagination for all cards
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCards, setTotalCards] = useState(0);

  // Load users for dropdown
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await fetchAdminUsers();
        const usersData = res.data.data || res.data;
        setUsers(usersData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Load all cards with pagination when viewMode is 'all'
  useEffect(() => {
    if (viewMode === 'all') {
      const loadAllCards = async () => {
        try {
          const res = await fetchAdminCards(page, limit);
          const cardsData = res.data.data || res.data;
          setAllCards(cardsData);
          if (res.data.pagination) {
            setTotalPages(res.data.pagination.pages || 1);
            setTotalCards(res.data.pagination.total || 0);
          }
        } catch (err) {
          console.error(err);
        }
      };
      loadAllCards();
    }
  }, [viewMode, page]);

  // Load user-specific cards when a user is selected
  useEffect(() => {
    if (viewMode === 'user' && selectedUserId) {
      const loadUserCards = async () => {
        try {
          const res = await fetchUserCards(selectedUserId);
          setUserCards(res.data);
        } catch (err) {
          console.error(err);
        }
      };
      loadUserCards();
    }
  }, [selectedUserId, viewMode]);

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
    setViewMode('user');
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getRarityBadgeColor = (rarity) => {
    switch (rarity) {
      case 'gold': return 'bg-yellow-600 text-white';
      case 'silver': return 'bg-gray-400 text-gray-900';
      case 'bronze': return 'bg-amber-700 text-white';
      default: return 'bg-blue-600 text-white';
    }
  };

  const cardsToDisplay = viewMode === 'all' ? allCards : userCards;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-white">Cards Management</h1>
        <div className="flex gap-3">
          <button
            onClick={() => { setViewMode('all'); setPage(1); }}
            className={`px-4 py-2 rounded-lg transition ${
              viewMode === 'all'
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            All Cards
          </button>
          <div className="relative">
            <select
              value={selectedUserId}
              onChange={handleUserChange}
              className="bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-yellow-500"
            >
              <option value="">Select User</option>
              {Array.isArray(users) && users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username} (ID: {user.id})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && viewMode === 'all' ? (
        <div className="text-white text-center py-10">Loading cards...</div>
      ) : viewMode === 'user' && !selectedUserId ? (
        <div className="text-white text-center py-10">Select a user to view their cards.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.isArray(cardsToDisplay) && cardsToDisplay.map((card) => (
              <div
                key={card.id}
                className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-yellow-500/50 transition-all"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-white">{card.name}</h3>
                    <p className="text-sm text-gray-400">{card.position}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRarityBadgeColor(card.rarity)}`}>
                    {card.rarity || 'common'}
                  </span>
                </div>

                {viewMode === 'user' && card.username && (
                  <p className="text-xs text-gray-400 mt-1">Owner: {card.username}</p>
                )}

                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  <div className="bg-gray-700 rounded p-1">
                    <div className="text-xs text-gray-400">ATK</div>
                    <div className="text-white font-bold">{card.attack}</div>
                  </div>
                  <div className="bg-gray-700 rounded p-1">
                    <div className="text-xs text-gray-400">DEF</div>
                    <div className="text-white font-bold">{card.defense}</div>
                  </div>
                  <div className="bg-gray-700 rounded p-1">
                    <div className="text-xs text-gray-400">SPD</div>
                    <div className="text-white font-bold">{card.speed}</div>
                  </div>
                  <div className="bg-gray-700 rounded p-1">
                    <div className="text-xs text-gray-400">ACC</div>
                    <div className="text-white font-bold">{card.accuracy}</div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {card.special_ability && (
                    <span className="bg-purple-900/50 text-purple-300 px-2 py-1 rounded-full">
                      ✨ {card.special_ability}
                    </span>
                  )}
                  {card.is_captain && (
                    <span className="bg-yellow-900/50 text-yellow-300 px-2 py-1 rounded-full">
                      👑 Captain
                    </span>
                  )}
                  {card.is_star && (
                    <span className="bg-blue-900/50 text-blue-300 px-2 py-1 rounded-full">
                      ⭐ Star
                    </span>
                  )}
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  Card ID: {card.id} | Template: {card.card_template_id}
                </div>
              </div>
            ))}
          </div>

          {viewMode === 'all' && totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6 text-white">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="text-sm">
                Page {page} of {totalPages} ({totalCards} cards)
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {viewMode !== 'all' && selectedUserId && (!Array.isArray(cardsToDisplay) || cardsToDisplay.length === 0) && (
            <div className="text-white text-center py-10">No cards found for this user.</div>
          )}
        </>
      )}
    </div>
  );
};

export default Cards;